import { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import { useHistory } from 'react-router';
import { createDistrict } from '../../../api/services/districtService';
import { getStates } from '../../../api/services/stateService';
import { getBillingSpecialists, getDivisions } from '../../../api/services/divisionService';
import { ResponseStatus } from '../../../components/notifications';
import { FieldInput } from '../../../components/fieldInput';
import Layout from '../../../components/layout';
import SubmitButton from '../../../components/submitButton';
import ReactPhoneInput from 'react-phone-input-2';
import { selectOption } from '../../../types/selectOption';
import User from '../../../api/models/user';
import SelectMulti from 'react-select';


const fetchBillingSpecialists = async () => await getBillingSpecialists();

const CreateDistrict = () => {
  const history = useHistory();

  // State
  const [name, setName] = useState('');
  const [visible, setVisible] = useState(false);
  const [divisionId, setDivisionId] = useState<number>(1);
  const [stateId, setStateId] = useState<number>(1);
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [phone, setPhone] = useState('');
  const [allowsDistrictTime, setAllowsDistrictTime] = useState(true);
  const [requiresMonthlyCaseNotes, setRequiresMonthlyCaseNotes] = useState(false);
  const [quickbooksName, setQuickbooksName] = useState('');
  //const [targetHours, setTargetHours] = useState<number>(450 / 60);
  const [fixedTargetHours, setFixedTargetHours] = useState(true);
  const [dailyAllowance, setDailyAllowance] = useState(800);
  const [dailyOverageAllowance, setDailyOverageAllowance] = useState(0);
  const [unlimitedOverageAllowed, setUnlimitedOverageAllowed] = useState(false);
  const [isMultiYearContract, setIsMultiYearContract] = useState(false);
  const [isInvoiceSummary] = useState(true);
  const [isInvoiceDetail] = useState(true);
  const [isCdcReport] = useState(true);
  const [deliveryMethod] = useState('');
  const [paymentBy] = useState('');
  const [invoiceDueDate] = useState(new Date());
  const [groupInvoiceSummaryBy] = useState('');
  const [groupInvoiceDetailBy] = useState('');
  const [notes] = useState('');
  const [billingSpecialists, setBillingSpecialists] = useState<User[]>([]);
  const [billingSpecialistsOptions, setBillingSpecialistsOptions] = useState<selectOption[]>([]);
  const [selectedDistrictBillingSpecialists, setSelectedDistrictBillingSpecialists] = useState<number[]>([]);
  const [selectedDistrictBillingSpecialistsOptions, setSelectedDistrictBillingSpecialistsOptions] = useState<any[]>([]);

  const statesData = useAsync({
    promiseFn: getStates,
  });

  const divisionsData = useAsync({
    promiseFn: getDivisions,
  });

  const saveDistrict = async () => {
    return await createDistrict({
      name: name,
      isVisible: visible,
      stateId: stateId,
      divisionId: divisionId,
      address1: address1,
      address2: address2,
      city: city,
      zipcode: zipcode,
      phone: phone,
      allowsDistrictTime: allowsDistrictTime,
      requiresMonthlyCaseNotes: requiresMonthlyCaseNotes,
      quickbooksName: quickbooksName,
      //targetHours: targetHours,
      fixedTargetHours: fixedTargetHours,
      dailyAllowance: dailyAllowance,
      dailyOverageAllowance: dailyOverageAllowance,
      unlimitedOverageAllowed: unlimitedOverageAllowed,
      contractReceivedDate: new Date(),
      contractExpirationYear: new Date(),
      isMultiYearContract: isMultiYearContract,
      isInvoiceDetail: isInvoiceDetail,
      isInvoiceSummary: isInvoiceSummary,
      isCdcReport: isCdcReport,
      deliveryMethod: deliveryMethod,
      paymentBy: paymentBy,
      invoiceDueDate: invoiceDueDate,
      groupInvoiceDetailBy: groupInvoiceDetailBy,
      groupInvoiceSummaryBy: groupInvoiceSummaryBy,
      notes: notes,
      contacts: [],
      billingSpecialistIds: selectedDistrictBillingSpecialists
    });
  };

  const save = useAsync({
    deferFn: saveDistrict,
    onResolve: (r) => {
      history.push(`/admin/districts/${r.data.district.districtId}`, {
        message: `"${name}" saved successfully!`,
      });
    },
    onReject: () => {
      window.scrollTo(0, 0);
    },
  });

  const billingSpecialistsData = useAsync({
    promiseFn: fetchBillingSpecialists,
    onResolve: (r) => {
      const b: User[] = r.data;
      const options: selectOption[] = [];
      setBillingSpecialists(b);

      //Loop through the disciplines to generate the options for the multi select
      (b.map((user) => (
        options.push({ value: user.userId ?? 0, label: `${user.lastName}, ${user.firstName}` })
      )));
      options.sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
      setBillingSpecialistsOptions(options);
    },
  });

  const handleDistrictBillingSpecialistsChange = (options: any) => {
    const newBs: number[] = [];
    const ops: selectOption[] = [];

    (options.map((opt: any) => {
      const bs = billingSpecialists.filter((bs) => opt.value === bs.userId)[0];
      newBs.push(bs.userId!);
      ops.push({ value: opt.value, label: opt.label });
      return true;
    }));

    setSelectedDistrictBillingSpecialists(newBs);
    setSelectedDistrictBillingSpecialistsOptions(ops);
  }

  const handleSubmit = () => {
    save.run();
  };

  return (
    <Fragment>
      <Layout
        isLoading={
          statesData.isPending ||
          divisionsData.isPending ||
          save.isPending ||
          billingSpecialistsData.isPending
        }>
        <Fragment>
          <ResponseStatus state={save} />
          <div>
            <div className='grid grid-cols-6 gap-6 mb-6'>
              <div className='col-span-6 sm:col-span-6'>
                <FieldInput
                  name='district'
                  label='Name'
                  minLength={1}
                  maxLength={70}
                  onChange={(e) => setName(e.target.value)}
                  value={name}
                />
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <div className='flex items-start'>
                  <div className='flex items-center h-5'>
                    <input
                      id='is-visible'
                      name='is-visible'
                      type='checkbox'
                      className='primary-checkbox'
                      checked={visible}
                      onChange={(e) => setVisible(e.currentTarget.checked)}
                    />
                  </div>
                  <div className='ml-3 text-sm'>
                    <label htmlFor='is-visible' className='primary'>
                      Is Visible
                    </label>
                    <p className='text-gray-500'>
                      When checked, this district will be available in the
                      timesheet entry flow.
                    </p>
                  </div>
                </div>
              </div>

              <div className='col-span-6 sm:col-span-6'>
                <FieldInput
                  name='address-field'
                  label='Address 1'
                  onChange={(e) => setAddress1(e.target.value)}
                  value={address1}
                />
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <FieldInput
                  name='address2-field'
                  label='Address 2'
                  onChange={(e) => setAddress2(e.target.value)}
                  value={address2}
                />
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <FieldInput
                  name='city-field'
                  label='City'
                  onChange={(e) => setCity(e.target.value)}
                  value={city}
                />
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <label className='primary'>
                  State<span className='text-red-500 pl-1'>*</span>
                </label>
                <select
                  onChange={(e) => setStateId(Number(e.target.value))}
                  className='block primary mt-1'
                  value={stateId}
                >
                  {statesData.data?.data.states.sort((a, b) => {
                    return a.name.localeCompare(b.name);
                  }).map((s) => (
                    <option key={`state_${s.stateId}`} value={s.stateId}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <FieldInput
                  name='zipcode-field'
                  label='Zip Code'
                  onChange={(e) => setZipcode(e.target.value)}
                  value={zipcode}
                />
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <ReactPhoneInput
                  country={'us'}
                  onChange={(phone) => setPhone(phone)}
                  value={phone}
                  disableDropdown={true}
                  disableSearchIcon={true}
                  disableCountryCode={true}
                  enableAreaCodes={true}
                  placeholder={'(555) 555 5555'}
                  specialLabel={'Phone'}
                  onlyCountries={["us"]}
                />
              </div>

              <div className='col-span-6 sm:col-span-6'>
                <label className='primary'>
                  Division<span className='text-red-500 pl-1'>*</span>
                </label>
                <select
                  onChange={(e) => setDivisionId(Number(e.target.value))}
                  className='block primary mt-1'
                  value={divisionId}
                >
                  {divisionsData.data?.data.divisions.sort((a, b) => {
                    return a.name.localeCompare(b.name);
                  }).map((d) => (
                    <option key={`division_${d.divisionId}`} value={d.divisionId}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <label className='primary'>
                  Billing Specialists<span className='text-red-500 pl-1'>*</span>
                </label>
                <SelectMulti
                  id="districtBillingSpecialistsOptions"
                  className='block primary'
                  isMulti={true}
                  key={`dbs-multi-${selectedDistrictBillingSpecialistsOptions.length}`}
                  options={billingSpecialistsOptions}
                  isClearable={true}
                  defaultValue={selectedDistrictBillingSpecialistsOptions}
                  onChange={(e) => handleDistrictBillingSpecialistsChange(e)}
                />
              </div>
            </div>
          </div>

          <div className="general-tab-form">
            <div className='col-span-6 sm:col-span-6 mb-3'>
              <div className='flex items-start'>
                <div className='flex items-center h-5'>
                  <input
                    id='allow-district-time'
                    name='allow-district-time'
                    type='checkbox'
                    className='primary-checkbox'
                    checked={allowsDistrictTime}
                    onChange={(e) => setAllowsDistrictTime(e.currentTarget.checked)}
                  />
                </div>
                <div className='ml-3 text-sm'>
                  <label htmlFor='allow-district-time' className='primary'>
                    Allows District Time
                  </label>
                </div>
              </div>
            </div>

            <div className='col-span-6 sm:col-span-6 mb-3'>
              <div className='flex items-start'>
                <div className='flex items-center h-5'>
                  <input
                    id='requires-monthly-case-notes'
                    name='requires-monthly-case-notes'
                    type='checkbox'
                    className='primary-checkbox'
                    checked={requiresMonthlyCaseNotes}
                    onChange={(e) => setRequiresMonthlyCaseNotes(e.currentTarget.checked)}
                  />
                </div>
                <div className='ml-3 text-sm'>
                  <label htmlFor='requires-monthly-case-notes' className='primary'>
                    Requires Monthly Case Notes
                  </label>
                </div>
              </div>
            </div>

            <div className='col-span-6 sm:col-span-6 mb-3'>
              <FieldInput
                name='quickbooks-name'
                label='QuickBooks Name'
                onChange={(e) => setQuickbooksName(e.target.value)}
                value={quickbooksName}
              />
            </div>

            <div className='col-span-6 sm:col-span-6 mb-3'>
              <div className='text-sm'>
                <label htmlFor='daily-allowance' className='primary'>
                  Daily Allowance
                </label>
              </div>
              <input
                type='text'
                name='daily-allowance'
                id="daily-allowance"
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => setDailyAllowance(Number(e.target.value))}
                value={dailyAllowance}
                placeholder="0"
                className='w-full'
              />
            </div>

            <div className='col-span-6 sm:col-span-6 mb-3'>
              <div className='text-sm'>
                <label htmlFor='requires-monthly-case-notes' className='primary'>
                  Overage Allowance
                </label>
              </div>
              <input
                type='text'
                name='overage-allowance'
                id="overage-allowance"
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => setDailyOverageAllowance(Number(e.target.value))}
                value={dailyOverageAllowance}
                placeholder="0"
                className='w-full'
              />
            </div>

            <div className='col-span-6 sm:col-span-6 mb-3'>
              <label className='primary'>
                Overage Allowed
              </label>
              <select
                onChange={(e) => setUnlimitedOverageAllowed(e.target.value === 'yes' ? true : false)}
                className='block primary mt-1'
                value={unlimitedOverageAllowed ? 'yes' : 'no'}
              >
                <option key="overageAllowed_Yes" value='yes'>Yes</option>
                <option key="overageAllowed_No" value='no'>No</option>
              </select>
            </div>

            <div className='col-span-6 sm:col-span-6 mb-3'>
              <div className='flex items-start'>
                <div className='flex items-center h-5'>
                  <input
                    id='is-multi-year-contract'
                    name='is-multi-year-contract'
                    type='checkbox'
                    className='primary-checkbox'
                    checked={isMultiYearContract}
                    onChange={(e) => setIsMultiYearContract(e.currentTarget.checked)}
                  />
                </div>
                <div className='ml-3 text-sm'>
                  <label htmlFor='multi-year-contract' className='primary'>
                    Multi-Year Contract?
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label>
              You can add Schools and Programs after you create the District.
            </label>
          </div>

          <hr className='py-4' />

          <div className='text-right'>
            <SubmitButton
              text='Save Changes'
              disabled={name.length === 0 || selectedDistrictBillingSpecialistsOptions.length === 0}
              spinning={save.isPending}
              onClick={handleSubmit}
            />
          </div>
        </Fragment>
      </Layout>
    </Fragment>
  );
};

export default CreateDistrict;
